import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { StaticQuery, graphql } from 'gatsby';
import { isEmpty } from 'lodash-es';

import { ActionCard, Data } from '../../components';

const StaticFeaturedArticles = ({
  featuredArticles,
  className,
  data: {
    staticPagesConfig: { childDataYaml: staticPagesConfig },
    allPosts: { edges: postsData },
    allTutorials: { edges: tutorialsData },
  },
}) => {
  const sectionTitle =
    featuredArticles && featuredArticles.heading
      ? featuredArticles.heading
      : staticPagesConfig.heading;
  const sectionBlurb =
    featuredArticles && featuredArticles.blurb
      ? featuredArticles.blurb
      : staticPagesConfig.blurb;
  const getFeaturedArticles = () => {
    // Figure out if the list was overriden from the specific static page
    // When creating within the NetlifyCMS it saves an empty object; even if you don't interact with it -- so doing a bit more validation
    const featuredArticlesTitlesSource =
      featuredArticles &&
      !isEmpty(featuredArticles.articles) &&
      featuredArticles.articles[0].title
        ? featuredArticles.articles
        : staticPagesConfig.articles;

    // Combine posts and tutorials into one, searchable array
    const featuredArticlesSource = [...postsData, ...tutorialsData];
    const featuredArticlesHydrated = [];

    // Loop through and attempt to find matching post/tutorial
    // If found, return data
    featuredArticlesTitlesSource.forEach(({ title }) => {
      const matchedArticle = featuredArticlesSource.find(
        ({ node }) => node.frontmatter.title === title
      );

      if (matchedArticle) featuredArticlesHydrated.push(matchedArticle.node);
    });

    return featuredArticlesHydrated;
  };

  const classes = cx('section section--large u-bgPanel', className);

  return (
    <section className={classes}>
      <div className="container">
        <div className="section-titleWrapper">
          <h3 className="section-title">
            <Data>{sectionTitle}</Data>
          </h3>
          <div className="section-titleSub">
            <Data markdown>{sectionBlurb}</Data>
          </div>
        </div>
        {getFeaturedArticles().length > 0 && (
          <ul className="blockGrid blockGrid-2@small blockGrid-3@medium blockGrid--largeGutter blockGrid--deepMatch">
            {getFeaturedArticles().map(node => (
              <li key={node.fileAbsolutePath}>
                <ActionCard
                  photo={node.fields.featuredImage}
                  hover
                  photoStretch="contain"
                  url={node.fields.fullUrl}
                >
                  <Data>{node.frontmatter.title}</Data>
                </ActionCard>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

StaticFeaturedArticles.propTypes = {
  featuredArticles: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
};

export default props => (
  <StaticQuery
    query={graphql`
      query StaticFeaturedArticlesdata {
        allTutorials: allMarkdownRemark(
          filter: {
            # Make sure we're only querying tutorials
            fileAbsolutePath: { regex: "/src/tutorials/" }
            frontmatter: { title: { ne: "BLUEPRINT" } }
          }
        ) {
          edges {
            node {
              ...TutorialTeaserFragment
            }
          }
        }

        allPosts: allMarkdownRemark(
          filter: {
            # Make sure we're only querying posts
            fileAbsolutePath: { regex: "/src/posts/" }
            frontmatter: { title: { ne: "BLUEPRINT" } }
          }
        ) {
          edges {
            node {
              ...PostTeaserFragment
            }
          }
        }

        staticPagesConfig: file(name: { eq: "static-pages" }) {
          childDataYaml {
            heading
            blurb
            articles {
              title
            }
          }
        }
      }
    `}
    render={data => <StaticFeaturedArticles data={data} {...props} />}
  />
);
